import { useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import { useQuery } from '@tanstack/react-query';

import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../../shared/ui/EstadoVazio';
import { opcoesDasClausulas, opcoesDasRegras, type ClausulaDoContrato, type RegraDoContrato } from '../api/mapeamento';
import { ListaDeRegras } from './RegrasDoContrato';
import { agruparPorSecao, type SecaoDoContrato } from './secoesDoContrato';

function paginas(inicio: number, fim: number): string {
  return inicio === fim ? `p. ${String(inicio)}` : `p. ${String(inicio)}–${String(fim)}`;
}

function quantasRegras(n: number): string {
  return n === 0 ? 'nenhuma regra' : n === 1 ? '1 regra' : `${String(n)} regras`;
}

function quantasClausulas(n: number): string {
  return n === 0 ? 'nenhuma cláusula numerada' : n === 1 ? '1 cláusula' : `${String(n)} cláusulas`;
}

function RegrasDaClausula({ regras }: { regras: readonly RegraDoContrato[] }): React.JSX.Element {
  if (regras.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nenhuma regra vigente saiu desta cláusula.
      </Typography>
    );
  }
  return <ListaDeRegras regras={regras} mostrarClausula={false} rotulo="Regras da cláusula" />;
}

/**
 * O texto completo da cláusula, fechado: as regras já citam as frases que importam, e repetir o texto todo em
 * cima delas faz a mesma frase aparecer duas vezes. Abre para quem quer conferir o que ficou de fora.
 */
function TextoDaClausula({ texto, abertoDeInicio }: { texto: string; abertoDeInicio: boolean }): React.JSX.Element {
  const [aberto, definirAberto] = useState(abertoDeInicio);
  if (texto === '') {
    return (
      <Typography variant="body2" color="text.secondary">
        Sem texto gravado para esta cláusula.
      </Typography>
    );
  }
  return (
    <Box>
      <Button
        size="small"
        sx={{ minWidth: 0, p: 0 }}
        aria-expanded={aberto}
        onClick={() => {
          definirAberto((atual) => !atual);
        }}
      >
        {aberto ? 'esconder texto da cláusula' : 'ver texto da cláusula'}
      </Button>
      {aberto ? (
        <Typography
          variant="body2"
          component="div"
          aria-label="Texto da cláusula"
          sx={{ mt: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 320, overflowY: 'auto', color: 'text.secondary' }}
        >
          {texto}
        </Typography>
      ) : null}
    </Box>
  );
}

function Clausula({ clausula, regras }: { clausula: ClausulaDoContrato; regras: readonly RegraDoContrato[] }): React.JSX.Element {
  const titulo = clausula.titulo !== null && clausula.titulo.trim() !== '' ? ` — ${clausula.titulo}` : '';
  return (
    <Accordion disableGutters variant="outlined" slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 1.5 }} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            Cláusula {clausula.numero}
            {titulo}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {paginas(clausula.paginaInicio, clausula.paginaFim)} · {quantasRegras(clausula.regrasVigentes)}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <RegrasDaClausula regras={regras} />
          {/* Sem regra, o texto é o único conteúdo da cláusula: já vem aberto. */}
          <TextoDaClausula texto={clausula.texto} abertoDeInicio={regras.length === 0} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function ForaDeClausula({ regras, descricao }: { regras: readonly RegraDoContrato[]; descricao: string }): React.JSX.Element {
  return (
    <Accordion disableGutters variant="outlined" slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Fora de cláusula numerada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {descricao} · {quantasRegras(regras.length)}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <RegrasDaClausula regras={regras} />
      </AccordionDetails>
    </Accordion>
  );
}

function ConteudoDaSecao({ secao, regras }: { secao: SecaoDoContrato; regras: readonly RegraDoContrato[] }): React.JSX.Element {
  return (
    <Stack spacing={1}>
      {secao.clausulas.map((clausula) => (
        <Clausula key={clausula.id} clausula={clausula} regras={regras.filter((r) => r.clausula?.id === clausula.id)} />
      ))}
      {secao.foraDeClausula.length > 0 ? (
        <ForaDeClausula
          regras={secao.foraDeClausula}
          descricao={secao.nome === null ? 'anexos e tabelas' : 'texto da seção sem número de cláusula'}
        />
      ) : null}
    </Stack>
  );
}

/**
 * As cláusulas das fontes ativas (`TP-067`, `D7`), organizadas pela seção do documento ("Anexo I – …", lida pelo
 * mapa): em cada seção, as cláusulas que começam nela (número, título, páginas e quantas regras vigentes) e as
 * regras dela fora de cláusula numerada. Abrir a cláusula mostra as regras; o texto completo fica atrás de "ver
 * texto da cláusula". Sem seção nenhuma no mapa, a lista é uma só, como o documento sem divisões.
 */
export function ClausulasDoContrato({ contratoId, competencia }: { contratoId: string; competencia: string }): React.JSX.Element {
  const consulta = useQuery(opcoesDasClausulas(contratoId, competencia));
  const consultaDasRegras = useQuery(opcoesDasRegras(contratoId, competencia));

  if (consulta.isPending || consultaDasRegras.isPending) return <EstadoDeCarregamento linhas={4} />;

  if (consulta.isError || consultaDasRegras.isError) {
    return (
      <EstadoDeErro
        erro={consulta.error ?? consultaDasRegras.error}
        contexto="Não foi possível carregar as cláusulas do contrato"
        aoReprocessar={() => {
          void consulta.refetch();
          void consultaDasRegras.refetch();
        }}
      />
    );
  }

  const clausulas = consulta.data;
  const regras = consultaDasRegras.data;
  const foraDeClausula = regras.filter((r) => r.clausula === null);

  if (clausulas.length === 0 && foraDeClausula.length === 0) {
    return (
      <EstadoVazio
        titulo="Nenhuma cláusula lida"
        descricao="As cláusulas aparecem depois que uma fonte do contrato é lida e ativada."
      />
    );
  }

  const secoes = agruparPorSecao(clausulas, foraDeClausula);
  if (secoes.every((s) => s.nome === null)) {
    return (
      <Stack spacing={1}>
        {secoes.map((secao) => (
          <ConteudoDaSecao key={secao.chave} secao={secao} regras={regras} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {secoes.map((secao) => {
        const nome = secao.nome ?? 'Sem seção identificada';
        const regrasDaSecao = secao.clausulas.reduce((soma, c) => soma + c.regrasVigentes, 0) + secao.foraDeClausula.length;
        return (
          <Box key={secao.chave} component="section" aria-label={nome}>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {nome}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {paginas(secao.paginaInicio, secao.paginaFim)} · {quantasClausulas(secao.clausulas.length)} ·{' '}
              {quantasRegras(regrasDaSecao)}
            </Typography>
            <ConteudoDaSecao secao={secao} regras={regras} />
          </Box>
        );
      })}
    </Stack>
  );
}
